import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Search, 
  X, 
  RefreshCw, 
  Sparkles,
  TrendingUp,
  Check
} from "lucide-react";

interface Category {
  id: number;
  name: string;
}

interface Brand {
  id: number;
  name: string;
}

interface MobileFilterControlsProps {
  nameFilter: string;
  setNameFilter: (value: string) => void;
  categoryFilter: string | string[];
  setCategoryFilter: (value: string | string[]) => void;
  brandFilter: string;
  setBrandFilter: (value: string) => void;
  priceRange: [number, number];
  setPriceRange: (value: [number, number]) => void;
  maxPrice: number;
  categories: Category[];
  brands: Brand[];
  resetFilters: () => void;
  onClose?: () => void;
}

const MobileFilterControls = ({
  nameFilter,
  setNameFilter,
  categoryFilter,
  setCategoryFilter,
  brandFilter,
  setBrandFilter,
  priceRange,
  setPriceRange,
  maxPrice,
  categories,
  brands = [],
  resetFilters,
  onClose,
}: MobileFilterControlsProps) => {
  const selectedCategories = Array.isArray(categoryFilter) 
    ? categoryFilter 
    : categoryFilter === "all" ? [] : [categoryFilter];

  const hasActiveFilters =
    nameFilter ||
    selectedCategories.length > 0 ||
    brandFilter !== "all" ||
    priceRange[0] > 0 ||
    priceRange[1] < maxPrice;

  const activeFiltersCount = [
    nameFilter,
    selectedCategories.length > 0,
    brandFilter !== "all",
    priceRange[0] > 0 || priceRange[1] < maxPrice,
  ].filter(Boolean).length;

  const popularCategories = categories.slice(0, 6);

  const handleCategoryToggle = (categoryId: string) => {
    if (categoryId === "all") {
      setCategoryFilter("all");
      setTimeout(() => onClose?.(), 150);
      return;
    }

    const newSelected = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    setCategoryFilter(newSelected.length > 0 ? newSelected : "all");
    setTimeout(() => onClose?.(), 150);
  };

  const handleBrandSelect = (value: string) => {
    setBrandFilter(value);
    setTimeout(() => onClose?.(), 150);
  };

  const handlePriceRangeSelect = (range: [number, number]) => {
    setPriceRange(range);
    setTimeout(() => onClose?.(), 150);
  };

  return (
    <div className="h-full bg-gradient-to-b from-white to-neutral-50 dark:from-neutral-900 dark:to-neutral-950 animate-slideInRight flex flex-col">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md border-b border-neutral-200 dark:border-neutral-700 p-4 z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-gradient rounded-2xl flex items-center justify-center animate-scaleIn">
              <Sparkles className="h-5 w-5 text-black" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                Filtros
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Selecciona múltiples opciones
              </p>
            </div>
          </div>
          {hasActiveFilters && (
            <Badge className="bg-brand-gradient text-black font-semibold px-3 py-1 animate-fadeIn">
              {activeFiltersCount}
            </Badge>
          )}
        </div>

        {/* Search Bar */}
        <div className="relative animate-fadeInUp">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-400" />
          <Input
            type="text"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder="Buscar productos..."
            className="pl-12 pr-12 h-12 bg-white dark:bg-neutral-800 border-2 border-neutral-200 dark:border-neutral-700 focus:border-brand-primary rounded-2xl text-base shadow-sm"
          />
          {nameFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setNameFilter("")}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-700"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Quick Categories Grid */}
        <div className="p-4 space-y-3 animate-fadeInUp" style={{animationDelay: '0.1s'}}>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-brand-primary" />
            <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
              Categorías populares
            </h3>
          </div>
          
          {/* All Categories Option */}
          <div className="mb-3">
            <Button
              variant={selectedCategories.length === 0 ? "default" : "outline"}
              onClick={() => handleCategoryToggle("all")}
              className={`w-full h-10 rounded-xl font-medium text-sm transition-all duration-200 ${
                selectedCategories.length === 0
                  ? "bg-brand-gradient text-black hover:shadow-brand"
                  : "border-2 border-neutral-200 dark:border-neutral-700 hover:border-brand-primary"
              }`}
            >
              <div className="flex items-center gap-2">
                {selectedCategories.length === 0 && <Check className="h-4 w-4" />}
                <span>✨ Todas las categorías</span>
              </div>
            </Button>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-2 gap-2">
            {popularCategories.map((category, index) => {
              const isSelected = selectedCategories.includes(category.id.toString());
              return (
                <Button
                  key={category.id}
                  variant={isSelected ? "default" : "outline"}
                  onClick={() => handleCategoryToggle(category.id.toString())}
                  className={`h-12 rounded-xl font-medium text-sm transition-all duration-200 animate-fadeInUp ${
                    isSelected
                      ? "bg-brand-gradient text-black hover:shadow-brand"
                      : "border-2 border-neutral-200 dark:border-neutral-700 hover:border-brand-primary"
                  }`}
                  style={{animationDelay: `${(index + 1) * 0.05}s`}}
                >
                  <div className="flex items-center gap-1 truncate">
                    {isSelected && <Check className="h-3 w-3 flex-shrink-0" />}
                    <span className="truncate">{category.name}</span>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>

        {/* All Categories Checklist */}
        {categories.length > 6 && (
          <div className="px-4 pb-4 space-y-3 animate-fadeInUp" style={{animationDelay: '0.2s'}}>
            <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
              Todas las categorías
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto bg-white dark:bg-neutral-800 rounded-2xl border-2 border-neutral-200 dark:border-neutral-700 p-3">
              {categories.map((category) => {
                const isSelected = selectedCategories.includes(category.id.toString());
                return (
                  <div key={category.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleCategoryToggle(category.id.toString())}
                      className="border-2"
                    />
                    <label 
                      onClick={() => handleCategoryToggle(category.id.toString())}
                      className="text-sm font-medium text-neutral-700 dark:text-neutral-300 cursor-pointer flex-1"
                    >
                      📂 {category.name}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Brands */}
        {brands.length > 0 && (
          <div className="px-4 pb-4 space-y-3 animate-fadeInUp" style={{animationDelay: '0.3s'}}>
            <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
              Marcas
            </h3>
            <Select onValueChange={handleBrandSelect} value={brandFilter}>
              <SelectTrigger className="h-12 border-2 border-neutral-200 dark:border-neutral-600 focus:border-brand-primary rounded-xl text-base">
                <SelectValue placeholder="Seleccionar marca" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="all" className="text-base py-3">
                  ⭐ Todas las marcas
                </SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id.toString()} className="text-base py-3">
                    🏢 {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Price Range - Quick Selection Only */}
        <div className="px-4 pb-4 space-y-4 animate-fadeInUp" style={{animationDelay: '0.4s'}}>
          <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
            Rango de precio
          </h3>
          
          {/* Current Price Display */}
          <div className="bg-white dark:bg-neutral-800 rounded-2xl p-4 border-2 border-neutral-200 dark:border-neutral-700 shadow-sm">
            <div className="text-center">
              <div className="text-xs text-neutral-500 mb-2">Rango seleccionado</div>
              <div className="text-2xl font-bold text-brand-primary">
                S/{priceRange[0]} - S/{priceRange[1]}
              </div>
            </div>
          </div>

          {/* Quick Price Ranges - Primary Interface */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
              Selecciona un rango
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={priceRange[0] === 0 && priceRange[1] === 100 ? "default" : "outline"}
                onClick={() => handlePriceRangeSelect([0, 100])}
                className={`h-12 text-sm border-2 rounded-xl justify-center transition-all ${
                  priceRange[0] === 0 && priceRange[1] === 100
                    ? "bg-brand-gradient text-black border-brand-primary hover:shadow-brand"
                    : "border-neutral-200 dark:border-neutral-700 hover:border-brand-primary"
                }`}
              >
                <div className="text-center">
                  <div className="text-lg">💸</div>
                  <div className="text-xs font-semibold">S/0 - S/100</div>
                </div>
              </Button>
              <Button
                variant={priceRange[0] === 100 && priceRange[1] === 300 ? "default" : "outline"}
                onClick={() => handlePriceRangeSelect([100, 300])}
                className={`h-12 text-sm border-2 rounded-xl justify-center transition-all ${
                  priceRange[0] === 100 && priceRange[1] === 300
                    ? "bg-brand-gradient text-black border-brand-primary hover:shadow-brand"
                    : "border-neutral-200 dark:border-neutral-700 hover:border-brand-primary"
                }`}
              >
                <div className="text-center">
                  <div className="text-lg">💰</div>
                  <div className="text-xs font-semibold">S/100 - S/300</div>
                </div>
              </Button>
              <Button
                variant={priceRange[0] === 300 && priceRange[1] === 500 ? "default" : "outline"}
                onClick={() => handlePriceRangeSelect([300, 500])}
                className={`h-12 text-sm border-2 rounded-xl justify-center transition-all ${
                  priceRange[0] === 300 && priceRange[1] === 500
                    ? "bg-brand-gradient text-black border-brand-primary hover:shadow-brand"
                    : "border-neutral-200 dark:border-neutral-700 hover:border-brand-primary"
                }`}
              >
                <div className="text-center">
                  <div className="text-lg">💎</div>
                  <div className="text-xs font-semibold">S/300 - S/500</div>
                </div>
              </Button>
              <Button
                variant={priceRange[0] === 500 && priceRange[1] === maxPrice ? "default" : "outline"}
                onClick={() => handlePriceRangeSelect([500, maxPrice])}
                className={`h-12 text-sm border-2 rounded-xl justify-center transition-all ${
                  priceRange[0] === 500 && priceRange[1] === maxPrice
                    ? "bg-brand-gradient text-black border-brand-primary hover:shadow-brand"
                    : "border-neutral-200 dark:border-neutral-700 hover:border-brand-primary"
                }`}
              >
                <div className="text-center">
                  <div className="text-lg">👑</div>
                  <div className="text-xs font-semibold">S/500+</div>
                </div>
              </Button>
            </div>
            
            {/* All Prices Option */}
            <Button
              variant={priceRange[0] === 0 && priceRange[1] === maxPrice ? "default" : "outline"}
              onClick={() => handlePriceRangeSelect([0, maxPrice])}
              className={`w-full h-10 text-sm border-2 rounded-xl justify-center transition-all mt-2 ${
                priceRange[0] === 0 && priceRange[1] === maxPrice
                  ? "bg-brand-gradient text-black border-brand-primary hover:shadow-brand"
                  : "border-neutral-200 dark:border-neutral-700 hover:border-brand-primary"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-base">✨</span>
                <span className="font-semibold">Todos los precios</span>
              </div>
            </Button>
          </div>
        </div>
      </div>

      {/* Footer Actions - Fixed */}
      <div className="flex-shrink-0 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md border-t border-neutral-200 dark:border-neutral-700 p-4 z-10 animate-fadeInUp" style={{animationDelay: '0.5s'}}>
        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="mb-3 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700">
            <div className="flex flex-wrap gap-1.5">
              {nameFilter && (
                <Badge className="bg-blue-500 text-white border-0 pr-1 text-xs">
                  "{nameFilter.length > 15 ? nameFilter.slice(0, 15) + '...' : nameFilter}"
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer hover:bg-blue-600 rounded-full p-0.5" 
                    onClick={() => setNameFilter("")}
                  />
                </Badge>
              )}
              {selectedCategories.length > 0 && selectedCategories.map(categoryId => {
                const category = categories.find(c => c.id.toString() === categoryId.toString());
                return category ? (
                  <Badge key={categoryId} className="bg-green-500 text-white border-0 pr-1 text-xs">
                    {category.name}
                    <X 
                      className="h-3 w-3 ml-1 cursor-pointer hover:bg-green-600 rounded-full p-0.5" 
                      onClick={() => handleCategoryToggle(categoryId)}
                    />
                  </Badge>
                ) : null;
              })}
              {brandFilter !== "all" && (
                <Badge className="bg-purple-500 text-white border-0 pr-1 text-xs">
                  {brands.find(b => b.id.toString() === brandFilter)?.name}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer hover:bg-purple-600 rounded-full p-0.5" 
                    onClick={() => handleBrandSelect("all")}
                  />
                </Badge>
              )}
              {(priceRange[0] > 0 || priceRange[1] < maxPrice) && (
                <Badge className="bg-orange-500 text-white border-0 pr-1 text-xs">
                  S/{priceRange[0]} - S/{priceRange[1]}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer hover:bg-orange-600 rounded-full p-0.5" 
                    onClick={() => handlePriceRangeSelect([0, maxPrice])}
                  />
                </Badge>
              )}
            </div>
          </div>
        )}

        <Button
          onClick={resetFilters}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition-all duration-300 ${
            hasActiveFilters
              ? "bg-brand-gradient text-black hover:shadow-brand-lg hover:scale-105"
              : "bg-neutral-200 dark:bg-neutral-700 text-neutral-500 cursor-not-allowed"
          }`}
          disabled={!hasActiveFilters}
        >
          <RefreshCw className="h-5 w-5 mr-3" />
          {hasActiveFilters 
            ? `Limpiar ${activeFiltersCount} filtro${activeFiltersCount > 1 ? 's' : ''}` 
            : "Sin filtros activos"
          }
        </Button>
      </div>
    </div>
  );
};

export default MobileFilterControls;